import { useParams, Navigate, Link } from "react-router-dom";
import { levels } from "@/data/content";
import PageShell from "./PageShell";
import RelatedLinks from "./RelatedLinks";
import LearningExplorer from "./LearningExplorer";
import PreschoolWorld from "./PreschoolWorld";
import Reveal from "@/components/base/Reveal";

// Her seviyeye özgü görsel vurgu — token bazlı, seviyeler birbirinden ayrışır.
interface LevelTone {
  badge: string;
  soft: string;
  text: string;
  border: string;
  dot: string;
}

const toneMap: Record<string, LevelTone> = {
  "okul-oncesi": {
    badge: "bg-accent-500 text-background-50",
    soft: "bg-accent-100/80",
    text: "text-accent-700",
    border: "border-accent-300",
    dot: "bg-accent-500",
  },
  ilkokul: {
    badge: "bg-primary-500 text-background-50",
    soft: "bg-primary-100/80",
    text: "text-primary-700",
    border: "border-primary-300",
    dot: "bg-primary-500",
  },
  ortaokul: {
    badge: "bg-secondary-500 text-background-50",
    soft: "bg-secondary-100/80",
    text: "text-secondary-700",
    border: "border-secondary-300",
    dot: "bg-secondary-500",
  },
  lise: {
    badge: "bg-foreground-950 text-background-50",
    soft: "bg-foreground-100",
    text: "text-foreground-900",
    border: "border-foreground-300",
    dot: "bg-foreground-950",
  },
};

function HeroStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
        {value}
      </span>
      <span className="text-xs md:text-sm text-foreground-500 leading-snug">{label}</span>
    </div>
  );
}

export default function LevelDetailPage() {
  const { slug } = useParams();
  const level = levels.find((l) => l.slug === slug);

  if (!level) return <Navigate to="/siniflar" replace />;

  if (level.slug === "okul-oncesi") return <PreschoolWorld />;

  const tone = toneMap[level.slug] ?? toneMap["okul-oncesi"];

  const crumbs = [
    { label: "Ana Sayfa", href: "/" },
    { label: "Sınıflar", href: "/siniflar" },
    { label: level.name },
  ];

  const related = levels
    .filter((l) => l.slug !== level.slug)
    .map((l) => ({ label: l.name, href: `/siniflar/${l.slug}` }));
  related.push({ label: "Sınavlara Hazırlık", href: "/sinavlar" });

  return (
    <PageShell crumbs={crumbs}>
      {/* Hero — split düzen: sol metin, sağ özgün görsel */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-12 md:py-16 border-b border-background-200">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <Reveal>
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold whitespace-nowrap ${tone.badge}`}
            >
              <i className="ri-sparkling-2-line" aria-hidden="true"></i>
              {level.shortName}
            </span>
            <h1 className="mt-5 text-3xl md:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-foreground-950 leading-[1.1] max-w-xl">
              {level.title}
            </h1>
            <p className="mt-4 text-lg md:text-xl text-foreground-800 font-medium leading-snug max-w-xl">
              {level.subtitle}
            </p>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed max-w-xl">
              {level.description}
            </p>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <Link
                to="/kayit"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold px-6 py-3 whitespace-nowrap transition-colors"
              >
                Ücretsiz Başla
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
              <Link
                to={level.slug === "ilkokul" ? "#icerik-kesfet" : level.slug === "ortaokul" ? "/sinavlar/lgs" : "/sinavlar/tyt"}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 hover:border-foreground-400 text-foreground-800 text-sm font-semibold px-6 py-3 whitespace-nowrap transition-colors"
              >
                {level.slug === "ilkokul" ? "Dersleri Keşfet" : level.slug === "ortaokul" ? "LGS Yolunu İncele" : "Üniversiteye Hazırlık"}
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {level.audience.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground-600 bg-background-50 border border-background-300 rounded-full px-3 py-1.5 whitespace-nowrap"
                >
                  <i className="ri-check-line text-foreground-400" aria-hidden="true"></i>
                  {a}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="relative rounded-2xl overflow-hidden border border-background-200 bg-background-50">
              <img
                src={level.heroImage}
                alt={`${level.name} öğrenme dünyası`}
                title={`${level.name} ${level.title}`}
                className="w-full h-[320px] md:h-[420px] lg:h-[480px] object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <LearningExplorer key={level.slug} level={level.slug} />
      {/* İstatistik bandı */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-8 md:py-10 border-b border-background-200">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
          {level.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 60}>
              <HeroStat value={s.value} label={s.label} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* Öne çıkan özellikler */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <span className="inline-flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-foreground-500">
              <span className={`w-5 h-px ${tone.dot}`} aria-hidden="true"></span>
              Neden bu seviye?
            </span>
            <h2 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              {level.name} için özel tasarlandı
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {level.features.map((f, i) => (
              <Reveal key={f.title} delay={i * 70}>
                <div className="h-full p-6 rounded-lg border border-background-200 bg-background-50 hover:border-background-300 transition-colors">
                  <span
                    className={`inline-flex w-11 h-11 items-center justify-center rounded-lg ${tone.soft} ${tone.text}`}
                  >
                    <i className={`${f.icon} text-xl`} aria-hidden="true"></i>
                  </span>
                  <h3 className="mt-4 font-heading font-semibold text-base text-foreground-950">
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ders alanları */}
      <section id="ders-alanlari" className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Ders alanları
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              Bu seviyede çalışacağın alanlar ve her birinde seni neler bekliyor.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {level.subjectDetails.map((s, i) => (
              <Reveal key={s.name} delay={i * 50}>
                <div className="flex items-start gap-4 p-5 rounded-lg border border-background-200 bg-background-50 hover:border-background-300 transition-colors">
                  <span
                    className={`inline-flex w-10 h-10 shrink-0 items-center justify-center rounded-md ${tone.soft} ${tone.text}`}
                  >
                    <i className={`${s.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold text-base text-foreground-950">
                      {s.name}
                    </h3>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{s.detail}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Öğrenme yolculuğu */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              Öğrenme yolculuğu
            </h2>
            <p className="mt-3 text-base text-foreground-600 leading-relaxed max-w-2xl">
              Karmaşık değil, net bir akış: her adım seni bir sonrakine hazırlar.
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-5 gap-4">
            {level.journey.map((step, i) => (
              <Reveal key={step.title} delay={i * 60}>
                <div className="relative h-full p-5 rounded-lg border border-background-200 bg-background-50">
                  <span
                    className={`inline-flex w-8 h-8 items-center justify-center rounded-full text-sm font-bold text-background-50 ${tone.dot}`}
                  >
                    {i + 1}
                  </span>
                  <h3 className="mt-3 font-heading font-semibold text-base text-foreground-950">
                    {step.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Haftalık program + içgörü */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
                Haftalık program örneği
              </span>
              <h2 className="mt-3 font-heading font-semibold text-lg md:text-xl text-foreground-950">
                Bir haftan böyle geçer
              </h2>
              <ul className="mt-5 space-y-3">
                {level.weeklyProgram.map((day) => (
                  <li key={day} className="flex items-start gap-3">
                    <span
                      className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${tone.dot}`}
                      aria-hidden="true"
                    ></span>
                    <span className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {day}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className={`h-full rounded-lg border ${tone.border} ${tone.soft} p-6 md:p-8`}>
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${tone.text}`}>
                <i className="ri-lightbulb-line text-sm" aria-hidden="true"></i>
                {level.insightTitle}
              </span>
              <h2 className="mt-3 font-heading font-semibold text-lg md:text-xl text-foreground-950">
                Bilmen gerekenler
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-800 leading-relaxed">
                {level.insight}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* İlgili seviyeler */}
      <section className="bg-background-50 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
            Diğer seviyeler
          </h2>
          <div className="mt-6">
            <RelatedLinks items={related} />
          </div>
        </div>
      </section>

      {/* Kapanış CTA */}
      <section className="bg-background-100 px-4 md:px-6 lg:px-10 py-14 md:py-20">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <div className="rounded-2xl bg-foreground-950 px-6 md:px-12 py-12 md:py-16 text-center">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 tracking-tight max-w-2xl mx-auto">
                {level.ctaTitle}
              </h2>
              <p className="mt-4 text-base md:text-lg text-background-50/70 leading-relaxed max-w-xl mx-auto">
                Seviyeni seç, rotanı kur; her gün ne çalışacağını bil.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/kayit"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-background-50 hover:bg-background-100 text-foreground-950 text-sm font-semibold px-7 py-3 whitespace-nowrap transition-colors"
                >
                  Hemen Başla
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <Link
                  to="/paketler"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-50/30 hover:border-background-50/60 text-background-50 text-sm font-semibold px-7 py-3 whitespace-nowrap transition-colors"
                >
                  Paketleri İncele
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}